/** @format */

import { Component, ElementRef, HostListener, Inject, Input, LOCALE_ID, OnChanges, OnDestroy, QueryList, SimpleChanges, ViewChild, ViewChildren } from "@angular/core";
import { formatDate, Location } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastService } from "src/app/services/toast/toast.service";
import { DatePipe } from "@angular/common";
import { LogSheetFormService } from "src/app/services/log-sheet-form/log-sheet-form.service";
import { ErrorHandlerService } from "src/app/services/errorHandler/error-handler.service";
import { DataPointService } from "src/app/services/data-point/data-point.service";
import { HttpEvent, HttpEventType } from "@angular/common/http";
import { LogsheetService } from "src/app/services/logsheet/logsheet.service";
import { SubmissionGuardService } from "src/app/services/submission-guard/submission-guard.service";
import { SubmissionGuard } from "src/app/models/submission-guard.model";
import { NotificationConfigService } from "src/app/services/notification-config/notification-config.service";
import { NotificationSendService } from "src/app/services/notification-send/notification-send.service";
import { isOperator } from "src/app/auth/roles";
import { environment } from "src/environments/environment";
import * as _ from "lodash";
import { RefreshStateService } from "src/app/services/refresh-state/refresh-state.service";

interface LogSheetRefreshSnapshot {
  logSheetId: number;
  sections: any[];
  deletedEntryIds: number[];
  sectionCount: number;
  submittedSectionCount: number;
  nextEntryId: number;
  hiddenFieldValueCache: Record<string, { value: any; note: string }>;
}

const FieldConditions = {
  EQUAL: "Equal (=)",
  NOT_EQUAL: "Not Equal (!=)",
  GREATER_THAN: "Greater than (>)",
  LESS_THAN: "Less than (<)",
  IS_EMPTY: "Is Empty",
  IS_NOT_EMPTY: "Is Not Empty",
  IS_NOT_SELECTED: "Is Not Selected",
  IS_SELECTED: "Is Selected",
  CONTAINS: "Contains",
  DOES_NOT_CONTAIN: "Does Not Contain",
  DOES_NOT_MATCH: "Does not match",
  MATCHES: "Matches"
}

const DataTypes = {
  DATE: "Date",
  DROPDOWN: "Dropdown List",
  FRACTION: "Fraction",
  GPS: "GPS Capture",
  SINGLE_SELECT_LIST: "List (Single Selection)",
  MULTI_SELECT_LIST: "List (Multiple Selection)",
  NUMBER: "Number",
  LONG_TEXT: "Text (Long)",
  SHORT_TEXT: "Text (Short)",
  TIMESTAMP: "Timestamp",
}

@Component({
  standalone: false,
  selector: "app-log-sheet-form",
  templateUrl: "./log-sheet-form.component.html",
  styleUrls: ["./log-sheet-form.component.css"],
})
export class LogSheetFormComponent implements OnChanges, OnDestroy {
  @ViewChildren('dpField') dpFields !: QueryList<ElementRef>;
  @Input() runtimeMode: boolean = false;
  @Input() runtimeEditable: boolean = true;
  // Per-field notes are allowed for anyone who can act on the item (e.g. an approver annotating
  // a field), independent of whether they can edit the field values.
  @Input() runtimeNotesAllowed: boolean = false;
  @Input() runtimeSections: any[] = [];
  isVersionMenuVisible: boolean = false;
  isNoteMenuVisible: boolean = false;
  selectedDataPointIdx: number = 0;
  selectedSectionIdx: number = 0;
  showNoteModal: boolean = false;
  note: string = "";
  showDeactivateMenu = false;
  showDeleteFormEntryMenu = false;
  pendingDeleteSectionId: number | null = null;
  activeVersion: number = 0;
  isSubmitting: boolean = false;
  hasSubmitted: boolean = false;
  isDeleting: boolean = false;
  isAddingSection: boolean = false;
  versions: any = [];
  baseUrl: string = environment.apiUrl;
  currentRoute: string = "";
  isSaving: boolean = false;
  currentRole: string = localStorage.getItem('role') ?? '';

  logSheet: any = {};

  lastModifiedDate = "";
  errorMessage: { success: boolean; message: string } = {
    success: true,
    message: "",
  };

  initialDataPointValues: any = [];
  dataPoints: any = [];
  initialDataPoints: any = []
  dataPointValues: any = [];
  currentState: any = [];
  private hiddenFieldValueCache: Record<string, { value: any; note: string }> = {};
  private refreshSnapshotTimer?: number;
  private refreshRestoreComplete = false;
  private refreshPersistenceDisabled = false;

  hideField = {
    display: "none"
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.runtimeMode && changes['runtimeSections'] && Array.isArray(this.runtimeSections)) {
      this.sections = this.runtimeSections;
    }
    if (this.runtimeMode && changes['runtimeEditable']) {
      this.activeVersion = this.runtimeEditable ? 0 : 1;
    }
    if (this.runtimeMode && changes['runtimeSections'] && Array.isArray(this.runtimeSections)) {
      // ensure the runtime form can compute durations and read values immediately
      this.stampDurations();
      // Rehydrate conditional-field visibility from already-saved values so a
      // reopened workflow instance reveals/hides dependent fields correctly
      // (the standard load path does this; runtime mode must too).
      this.applyInitialConditionalVisibility();
    }
  }

  runtimeFieldClasses(dataPoint: any): Record<string, boolean> {
    const type = (dataPoint?.['dataType'] ?? '').toString();
    return {
      'runtime-field-cell': true,
      'runtime-field-cell--wide': [
        'Text (Long)',
        'Date Range',
        'Duration',
        'GPS Capture',
        'Timestamp'
      ].includes(type),
      'runtime-field-cell--compact': [
        'Date',
        'Number',
        'Fraction',
        'Dropdown List',
        'File Upload',
        'Timestamp (Auto)',
        'Reference Number'
      ].includes(type),
      'runtime-field-cell--boolean': type === 'Boolean'
    };
  }
  /**
   * A field is locked (non-editable) when an old version is being viewed (activeVersion != 0)
   * OR the field itself is flagged read-only / mirror (e.g. workflow mirror fields). The
   * template previously only honoured activeVersion, so per-field read-only had no effect.
   */
  public isLocked(dataPoint?: any): boolean {
    return this.activeVersion != 0 || !!dataPoint?.['readonly'];
  }

  /**
   * Runtime entry point for the workflow runtime form, which bypasses the normal
   * saveLogSheet path. Stamps auto-generated fields (Duration, Reference Number,
   * Timestamp) so they carry their real values into the payload, then runs inline
   * per-field validation. Returns true only when there are no invalid fields.
   */
  public prepareAndValidateForSubmit(): boolean {
    this.stampDurations();
    this.stampReferenceNumbers();
    this.stampAutoTimestamps();
    this.validateFields();
    return Object.keys(this.invalidFields).length === 0;
  }

  public getCurrentFieldValues(): Record<string, any> {
    const values: Record<string, any> = {};
    for (const section of this.sections || []) {
      for (let idx = 0; idx < (section.dataPointValues || []).length; idx++) {
        const fieldValue = section.dataPointValues[idx];
        const fieldMeta = section.dataPoints?.[idx];
        const valueId = fieldMeta?.valueId ?? fieldMeta?.dataPointName ?? `field_${idx + 1}`;
        values[valueId] = fieldValue?.value ?? '';
      }
    }
    return values;
  }

  // Per-field notes keyed by the same valueId as getCurrentFieldValues, so they persist alongside
  // the payload and can be re-hydrated on load.
  public getCurrentFieldNotes(): Record<string, string> {
    const notes: Record<string, string> = {};
    for (const section of this.sections || []) {
      for (let idx = 0; idx < (section.dataPointValues || []).length; idx++) {
        const fieldMeta = section.dataPoints?.[idx];
        const valueId = fieldMeta?.valueId ?? fieldMeta?.dataPointName ?? `field_${idx + 1}`;
        const note = (section.dataPointValues[idx]?.note ?? '').toString().trim();
        if (note) notes[valueId] = note;
      }
    }
    return notes;
  }

  private isFieldConditionMet(currentValue: any, sourceFieldDataType: string, condition: string, conditionValue?: any): boolean {
    let conditionMet: boolean = false;
    if (condition == FieldConditions.IS_EMPTY) {
      if (String(currentValue).trim() === "") {
        conditionMet = true;
      }
      return conditionMet;
    } else if (condition == FieldConditions.IS_NOT_EMPTY) {
      if (String(currentValue).trim() !== "") {
        conditionMet = true;
      }
      return conditionMet;
    }

    if (sourceFieldDataType == DataTypes.NUMBER) {
      currentValue = Number(currentValue);
      conditionValue = Number(conditionValue);
    } else if (sourceFieldDataType == DataTypes.FRACTION) {
      currentValue = this.fractionToNumber(currentValue);
      conditionValue = this.fractionToNumber(conditionValue);
    } else if (sourceFieldDataType == DataTypes.DATE) {
      currentValue = this.parseDateForEval(currentValue).getTime();
      conditionValue = this.parseDateForEval(conditionValue).getTime();
    } else if (sourceFieldDataType == DataTypes.TIMESTAMP) {
      currentValue = new Date(currentValue).getTime();
      conditionValue = new Date(conditionValue).getTime();
    } else {
      currentValue = String(currentValue).toLowerCase();
      conditionValue = String(conditionValue).toLowerCase();
    }

    if (condition == FieldConditions.EQUAL || condition == FieldConditions.MATCHES) {
      if (currentValue == conditionValue) { conditionMet = true; };
    } else if (condition == FieldConditions.NOT_EQUAL || condition == FieldConditions.DOES_NOT_MATCH) {
      if (currentValue != conditionValue) { conditionMet = true; }
    } else if (condition == FieldConditions.GREATER_THAN) {
      if (currentValue > conditionValue) { conditionMet = true; }
    } else if (condition == FieldConditions.LESS_THAN) {
      if (currentValue < conditionValue) { conditionMet = true; }
    } else if (condition == FieldConditions.CONTAINS) {
      if (currentValue.includes(conditionValue)) { conditionMet = true }
    } else if (condition == FieldConditions.DOES_NOT_CONTAIN) {
      if (!currentValue.includes(conditionValue)) { conditionMet = true; }
    } else if (condition == FieldConditions.IS_SELECTED) {
      if (sourceFieldDataType == DataTypes.SINGLE_SELECT_LIST || sourceFieldDataType == DataTypes.DROPDOWN) {
        if (String(conditionValue).split(",").includes(currentValue)) {
          conditionMet = true;
        }
      } else {//If it is a multi-select list
        if (conditionValue.split(",").some((condVal: any) => currentValue.split(",").includes(condVal))) {
          conditionMet = true;
        }
      }
    } else if (condition == FieldConditions.IS_NOT_SELECTED) {
      if (sourceFieldDataType == DataTypes.SINGLE_SELECT_LIST || sourceFieldDataType == DataTypes.DROPDOWN) {
        if (!conditionValue.split(",").includes(currentValue)) {
          conditionMet = true;
        }
      } else {//If it is a multi select
        if (!conditionValue.split(",").some((condVal: any) => currentValue.split(",").includes(condVal))) {
          conditionMet = true;
        }
      }
    }
    return conditionMet;
  }

  //boost this up
  evaluateFieldCondition(currentValue: any, sourceFieldDataType: string, condition: string, hiddenFieldDataPoint: number, sectionIdx: number, conditionValue?: any) {
    const conditionMet = this.isFieldConditionMet(
      currentValue,
      sourceFieldDataType,
      condition,
      conditionValue
    );
    conditionMet ? this.showHiddenField(hiddenFieldDataPoint, sectionIdx) : this.hideHiddenField(hiddenFieldDataPoint, sectionIdx);
  }

  showHiddenField(hiddenFieldDataPoint: number, sectionIdx: number) {
    const hiddenFieldId = Number(hiddenFieldDataPoint);
    const hiddenField = this.sections[sectionIdx].dataPoints.find((dp: any) => Number(dp.dataPointId) === hiddenFieldId);
    if (!hiddenField) {
      return;
    }
    hiddenField["hiddenByDefault"] = false;
    const hiddenValue = this.sections[sectionIdx].dataPointValues.find((dp: any) => Number(dp.dataPointId) === hiddenFieldId);
    const cacheKey = this.getHiddenFieldCacheKey(sectionIdx, hiddenFieldId);
    const cachedValue = this.hiddenFieldValueCache[cacheKey];
    if (cachedValue && hiddenValue) {
      hiddenValue.value = this.cloneHiddenFieldValue(cachedValue.value);
      hiddenValue.note = cachedValue.note;
      delete this.hiddenFieldValueCache[cacheKey];
    }

    const target = this.dpFields.find(el =>
      el.nativeElement.getAttribute('data-id') == hiddenFieldId.toString() &&
      el.nativeElement.getAttribute('data-section') == sectionIdx.toString()
    );

    if (target) {
      target.nativeElement.classList.add('initialHiddenFieldPosition');
    }

    requestAnimationFrame(() => {
      if (target) {
        target.nativeElement.classList.add('showHiddenField');
        target.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  hideHiddenField(hiddenFieldDataPoint: number, sectionIdx: number) {
    const hiddenFieldId = Number(hiddenFieldDataPoint);
    const target = this.dpFields.find(el =>
      el.nativeElement.getAttribute('data-id') == hiddenFieldId.toString() &&
      el.nativeElement.getAttribute('data-section') == sectionIdx.toString()
    );

    if (target) {
      target.nativeElement.classList.remove('showHiddenField');
      target.nativeElement.classList.add('initialHiddenFieldPosition');
    }

    setTimeout(() => {
      if (!target?.nativeElement.classList.contains('showHiddenField')) {
        const hiddenField = this.sections[sectionIdx].dataPoints.find((dp: any) => Number(dp.dataPointId) === hiddenFieldId);
        const hiddenValue = this.sections[sectionIdx].dataPointValues.find((dp: any) => Number(dp.dataPointId) === hiddenFieldId);
        if (hiddenField) {
          hiddenField["hiddenByDefault"] = true;
        }
        if (hiddenValue) {
          this.cacheHiddenFieldValue(sectionIdx, hiddenFieldId, hiddenValue);
          hiddenValue.value = "";
          hiddenValue.note = "";
        }
      }
    }, 1500);
  }

  private getHiddenFieldCacheKey(sectionIdx: number, dataPointId: number): string {
    return `${sectionIdx}:${dataPointId}`;
  }

  private cloneHiddenFieldValue(value: any): any {
    return Array.isArray(value) ? [...value] : value;
  }

  private hasHiddenFieldValue(fieldValue: any): boolean {
    const value = fieldValue?.value;
    const hasValue = Array.isArray(value)
      ? value.length > 0
      : value != null && String(value).trim() !== "";
    const hasNote = (fieldValue?.note ?? "").trim() !== "";
    return hasValue || hasNote;
  }

  private cacheHiddenFieldValue(sectionIdx: number, dataPointId: number, fieldValue: any): void {
    const cacheKey = this.getHiddenFieldCacheKey(sectionIdx, dataPointId);
    if (this.hiddenFieldValueCache[cacheKey] && !this.hasHiddenFieldValue(fieldValue)) {
      return;
    }

    this.hiddenFieldValueCache[cacheKey] = {
      value: this.cloneHiddenFieldValue(fieldValue.value),
      note: fieldValue.note ?? "",
    };
  }

  parseDateForEval = (strDate: string) => {
    const [day, month, year] = strDate.split("-");

    const months: any = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3,
      May: 4, Jun: 5, Jul: 6, Aug: 7,
      Sep: 8, Oct: 9, Nov: 10, Dec: 11
    };

    return new Date(Number(year), months[month], Number(day));
  }

  fractionToNumber(fraction: string) {
    const [numerator, denominator] = fraction.split("/");
    return (Number(numerator) / Number(denominator));
  }
  // Section management
  sections: any = [];
  sectionCount: number = 1;
  submittedSectionCount: number = 0; // Track how many sections have been submitted
  nextEntryId: number = 1; // Track the next entry ID to use
  isDynamicForm: boolean = false; // Track if form is dynamic
  deletedEntryIds: number[] = []; // Track deleted entry IDs

  // Guard evaluation
  guards: SubmissionGuard[] = [];
  guardsLoading: boolean = false;
  guardsLoaded: boolean = false;
  perItemConfig: any = null;
  submissionConfig: any = null;
  guardQueue: SubmissionGuard[] = [];
  activeGuard: SubmissionGuard | null = null;
  activeGuardMessage: string = '';
  pendingSubmitCallback: (() => void) | null = null;

  deactivateText = {
    title: "Delete Note",
    message1: "",
    message2: "",
  };

  deleteFormEntry = {
    title: 'Delete Form Entry',
    message1: 'Deleting this entry will permanently remove all historical submission data logged against it.',
    message2: 'This action is irreversible. Do you wish to proceed?',
  }

  errorMsgEmpty = "This field is empty, please input a value or add a note";
  errorMsgNAN = "Please enter a valid number";
  errorMsgMinValue = "Please enter at least 2 characters";
  errorMsgDenominator = "The Denominator cannot be set to zero";
  errorIntegerOnly = "Only integers are accepted";
  errorMsgRange =
    "This entry is out of range. Please revise entry or leave a note.";
  errorMsgInvalidDate = "Invalid date. Use dd-MMM-yyyy format (e.g., 29-Jan-2026). Year must be between 1900-2100.";

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private route: ActivatedRoute,
    private router: Router,
    public toastService: ToastService,
    private logSheetFormService: LogSheetFormService,
    private logSheetService: LogsheetService,
    private errorService: ErrorHandlerService,
    private location: Location,
    private datePipe: DatePipe,
    private guardService: SubmissionGuardService,
    private notificationConfigService: NotificationConfigService,
    private notificationSendService: NotificationSendService,
    private dataPointService: DataPointService,
    private refreshState: RefreshStateService
  ) {
    this.currentRoute = this.router.url;
  }

  // File Upload helpers. Config lives in listOptions as "types,maxFiles,maxSizeMB";
  // the value is a ";"-separated list of "fileId|fileName" entries.
  private parseFileCfg(dp: any): { types: string; maxFiles: number; maxSizeMB: number } {
    const cfg = (dp?.['listOptions'] || 'any,1,10').split(',');
    return {
      types: (cfg[0] || 'any').trim(),
      maxFiles: Math.max(1, parseInt(cfg[1], 10) || 1),
      maxSizeMB: Math.max(1, parseInt(cfg[2], 10) || 10),
    };
  }

  fileValueList(value: string): { id: string; name: string }[] {
    if (!value) return [];
    return value.split(';').filter((v) => v).map((part) => {
      const [id, name] = part.split('|');
      return { id, name: name || id };
    });
  }

  // Downloads via HttpClient so the auth interceptor adds the JWT, then saves
  // the returned blob. A plain anchor navigation would hit the endpoint without
  // a token and fail with 401.
  downloadFile(f: { id: string; name: string }): void {
    this.dataPointService.downloadDataPointFile(f.id).subscribe({
      next: (resp) => {
        const blob = resp.body;
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = f.name;
        a.click();
        URL.revokeObjectURL(url);
      },
    });
  }

  fileAccept(dp: any): string {
    const types = this.parseFileCfg(dp).types;
    return types && types.toLowerCase() !== 'any' ? types : '';
  }

  fileAllowsMultiple(dp: any): boolean {
    return this.parseFileCfg(dp).maxFiles > 1;
  }

  // Per-field UI state, keyed by "sectionIdx_idx".
  fileErrors: { [key: string]: string } = {};
  fileUploads: { [key: string]: { name: string; progress: number }[] } = {};

  private fileKey(sectionIdx: number, idx: number): string {
    return `${sectionIdx}_${idx}`;
  }
  fileErrorFor(sectionIdx: number, idx: number): string {
    return this.fileErrors[this.fileKey(sectionIdx, idx)] || '';
  }
  fileUploadsFor(sectionIdx: number, idx: number): { name: string; progress: number }[] {
    return this.fileUploads[this.fileKey(sectionIdx, idx)] || [];
  }
  // True when stored + in-flight files have reached the field's max.
  fileAtMax(dp: any, sectionIdx: number, idx: number, value: string): boolean {
    const used = this.fileValueList(value).length + this.fileUploadsFor(sectionIdx, idx).length;
    return used >= this.parseFileCfg(dp).maxFiles;
  }

  // Validate against the field's type/size/count limits, then upload each file
  // with a live progress bar; failures show an inline error under the field.
  onDataPointFileSelected(event: Event, sectionIdx: number, idx: number, dp: any): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const key = this.fileKey(sectionIdx, idx);
    this.fileErrors[key] = '';
    const cfg = this.parseFileCfg(dp);
    const cell = this.sections[sectionIdx].dataPointValues[idx];
    const allowed = cfg.types.toLowerCase() === 'any'
      ? [] : cfg.types.split(',').map((t) => t.trim().toLowerCase()).filter((t) => t);
    let count = this.fileValueList(cell['value']).length + this.fileUploadsFor(sectionIdx, idx).length;

    for (const file of Array.from(input.files)) {
      if (count >= cfg.maxFiles) { this.fileErrors[key] = `Up to ${cfg.maxFiles} file(s) allowed`; break; }
      if (file.size > cfg.maxSizeMB * 1024 * 1024) { this.fileErrors[key] = `${file.name} exceeds the ${cfg.maxSizeMB}MB limit`; continue; }
      if (allowed.length && !allowed.some((ext) => file.name.toLowerCase().endsWith(ext))) {
        this.fileErrors[key] = `${file.name} is not an allowed file type`; continue;
      }
      count++;
      const entry = { name: file.name, progress: 0 };
      (this.fileUploads[key] = this.fileUploads[key] || []).push(entry);
      this.dataPointService.uploadDataPointFile(file).subscribe({
        next: (ev: HttpEvent<any>) => {
          if (ev.type === HttpEventType.UploadProgress && ev.total) {
            entry.progress = Math.round((ev.loaded / ev.total) * 100);
          } else if (ev.type === HttpEventType.Response) {
            const res = ev.body;
            cell['value'] = (cell['value'] ? cell['value'] + ';' : '') + `${res.dataPointFileId}|${res.fileName}`;
            this.removeFileUpload(key, entry);
            this.onInputChange(sectionIdx);
          }
        },
        error: () => { this.removeFileUpload(key, entry); this.fileErrors[key] = `Upload of ${file.name} failed`; },
      });
    }
    input.value = '';
  }

  private removeFileUpload(key: string, entry: { name: string; progress: number }): void {
    const arr = this.fileUploads[key];
    if (!arr) return;
    const i = arr.indexOf(entry);
    if (i >= 0) arr.splice(i, 1);
  }

  removeDataPointFile(sectionIdx: number, idx: number, fileIdx: number): void {
    const cell = this.sections[sectionIdx].dataPointValues[idx];
    const parts = (cell['value'] || '').split(';').filter((v: string) => v);
    parts.splice(fileIdx, 1);
    cell['value'] = parts.join(';');
    this.onInputChange(sectionIdx);
  }

  // Duration (self-contained variant): its own value holds "start|end" dates and
  // listOptions holds the unit. Returns the elapsed time formatted in that unit,
  // or '' when either date is missing/invalid (so the field shows a placeholder).
  computeDuration(value: string, listOptions: string): string {
    const [start, end] = (value || '').split('|');
    return this.computeDurationBetween(start, end, listOptions);
  }

  // Shared core: elapsed time between two date strings, formatted in the unit ('' when invalid).
  private computeDurationBetween(start: string, end: string, unit: string): string {
    if (!start || !end) return '';
    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();
    if (isNaN(startMs) || isNaN(endMs)) return '';
    const u = (unit || 'days').trim();
    const divisor = u === 'minutes' ? 60000 : u === 'hours' ? 3600000 : 86400000;
    const effectiveUnit = u === 'minutes' || u === 'hours' ? u : 'days';
    return `${Math.round(((endMs - startMs) / divisor) * 100) / 100} ${effectiveUnit}`;
  }

  // Reference-based Duration (workflow): listOptions packs "startDpId,endDpId,unit" and the
  // value is derived from two OTHER date fields in the form rather than its own pickers.
  // Returns the parsed config, or null for the self-contained variant (listOptions = unit only).
  durationRefConfig(listOptions: string): { startId: number; endId: number; unit: string } | null {
    const parts = (listOptions || '').split(',').map((p) => p.trim());
    if (parts.length < 3) return null;
    const startId = Number(parts[0]);
    const endId = Number(parts[1]);
    if (!Number.isFinite(startId) || !Number.isFinite(endId)) return null;
    return { startId, endId, unit: parts[2] || 'days' };
  }

  isReferenceDuration(dataPoint: any): boolean {
    return dataPoint?.['dataType'] === 'Duration' && !!this.durationRefConfig(dataPoint?.['listOptions']);
  }

  // Value of a sibling data point (by its dataPointId) within the same section.
  private valueByDataPointId(section: any, dataPointId: number): string {
    const i = (section?.dataPoints || []).findIndex((dp: any) => dp?.['dataPointId'] === dataPointId);
    return i >= 0 ? (section.dataPointValues?.[i]?.['value'] ?? '') : '';
  }

  // Computed, read-only duration for the reference-based variant: (End − Start) in the unit.
  computeReferenceDuration(section: any, dataPoint: any): string {
    const cfg = this.durationRefConfig(dataPoint?.['listOptions']);
    if (!cfg) return '';
    const start = this.valueByDataPointId(section, cfg.startId);
    const end = this.valueByDataPointId(section, cfg.endId);
    return this.computeDurationBetween(start, end, cfg.unit);
  }



  stringToListConverter(list: string) {
    if (list === "" || list == null) {
      return [];
    }
    return list.split(",").map((e: any) => e.trim());
  }

  handleMultiSelect(dataPointIdx: any, option: string, sectionIdx?: number, valuationOptions?: any) {
    const dataPointValues = sectionIdx !== undefined
      ? this.sections[sectionIdx].dataPointValues
      : this.dataPointValues;

    let selectedValues = this.stringToListConverter(
      dataPointValues[dataPointIdx]?.value
    );

    if (selectedValues?.indexOf(option) != -1) {
      selectedValues = [
        ...selectedValues?.filter((e: any) => e.trim() != option),
      ];
    } else {
      selectedValues = [...selectedValues, option];
    }
    dataPointValues[dataPointIdx]["value"] = selectedValues?.join(",");
    // Mark section as unsaved when value changes
    if (sectionIdx !== undefined) {
      this.sections[sectionIdx].isSubmitted = false;
    }

    if (valuationOptions) {
      this.evaluateFieldCondition(
        dataPointValues[dataPointIdx]["value"],
        valuationOptions?.[0],
        valuationOptions?.[1],
        valuationOptions?.[2],
        valuationOptions?.[3],
        valuationOptions?.[4]
      )
    }
  }

  // Mark section as unsaved when any input changes
  onInputChange(sectionIdx?: number): void {
    // Mark section as unsaved when value changes
    if (sectionIdx !== undefined) {
      this.sections[sectionIdx].isSubmitted = false;
    }
  }

  // Date Range values are stored as "start|end". Update one side and rejoin.
  setDateRangePart(sectionIdx: number, idx: number, part: 'start' | 'end', val: string): void {
    const parts = (this.sections[sectionIdx].dataPointValues[idx]['value'] || '|').split('|');
    if (part === 'start') parts[0] = val; else parts[1] = val;
    this.sections[sectionIdx].dataPointValues[idx]['value'] = `${parts[0] || ''}|${parts[1] || ''}`;
    this.onInputChange(sectionIdx);
  }

  // Normalize text: convert CRLF to LF for consistent counting across platforms
  normalizeText(text: string): string {
    if (!text) return '';
    return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  }

  // Count actual user-perceived characters (handles emojis and Unicode correctly)
  countCharacters(text: string | null | undefined): number {
    if (!text) return 0;
    const normalized = this.normalizeText(text);
    // Use Array.from to properly count Unicode graphemes
    return Array.from(normalized).length;
  }

  // Handle LONG_TEXT input changes with normalization
  onLongTextChange(
    value: string,
    sectionIdx: number,
    dataPointIdx: number,
    section: any,
    valuationOptions?: any
  ): void {
    // Normalize line endings for consistent counting
    const normalized = this.normalizeText(value);
    section.dataPointValues[dataPointIdx]['value'] = normalized;
    if (valuationOptions) {
      this.evaluateFieldCondition(
        normalized,
        valuationOptions?.[0],
        valuationOptions?.[1],
        valuationOptions?.[2],
        valuationOptions?.[3],
        valuationOptions?.[4]
      )
    }
    // Mark section as unsaved
    section.isSubmitted = false;
  }

  isMultiSelected(dataPointIdx: any, option: string, sectionIdx?: number) {
    if (sectionIdx !== undefined && this.activeVersion == 0) {
      return this.stringToListConverter(
        this.sections[sectionIdx].dataPointValues[dataPointIdx]["value"]
      )?.indexOf(option) >= 0;
    }

    if (
      (this.activeVersion == 0 &&
        this.stringToListConverter(
          this.dataPointValues?.[dataPointIdx]["value"]
        )?.indexOf(option) >= 0) ||
      (this.activeVersion > 0 &&
        this.stringToListConverter(
          this.versions?.[dataPointIdx]?.["dataPointValue"][
          this.activeVersion
          ]?.["value"]
        )?.indexOf(option) != -1)
    ) {
      return true;
    }
    return false;
  }

  isSingleSelected(dataPointIdx: any, option: string, sectionIdx?: number) {
    if (sectionIdx !== undefined && this.activeVersion == 0) {
      return String(this.sections[sectionIdx].dataPointValues[dataPointIdx]["value"] ?? "")
        .toLowerCase() == option.toLowerCase();
    }

    if (
      (this.activeVersion == 0 &&
        this.dataPointValues?.[dataPointIdx]["value"].toLowerCase() ==
        option.toLowerCase()) ||
      (this.activeVersion > 0 &&
        this.versions?.[dataPointIdx]?.["dataPointValue"][this.activeVersion]?.[
          "value"
        ]
          .toLowerCase()
          .trim() == option.toLowerCase())
    ) {
      return true;
    }
    return false;
  }
  goBack() {
    window.history.back();
  }

  singleSelect: any = {};
  // handleSingleSelect(idx: any, option: any, sectionIdx?: number) {
  //   const dataPointValues = sectionIdx !== undefined 
  //     ? this.sections[sectionIdx].dataPointValues 
  //     : this.dataPointValues;
  //   dataPointValues[idx].value = option.trim();
  // }

  handleSingleSelect(idx: any, option: any, sectionIdx?: number) {
    // Guard: prevent edits on previous versions
    if (this.activeVersion != 0) return;

    // Allow editing for both dynamic and static forms
    if (sectionIdx !== undefined) {
      this.sections[sectionIdx].dataPointValues[idx].value = option.trim();
      this.onInputChange(sectionIdx);
      return;
    }
  }

  isChecked(idx: number, option: string): boolean {
    return this.dataPointValues[idx].value === option;
  }

  private initFromLogSheet(logSheet: any): void {
    this.logSheet = logSheet ?? {};


    // Only show Add/Remove section controls for explicitly-dynamic logsheets
    const formType = this.logSheet?.formType || this.logSheet?.logSheetTemplated?.formType;
    this.isDynamicForm =
      (formType ?? "").toString().toLowerCase().trim() === "dynamic";
    const scheduleType: string | undefined =
      this.logSheet?.logSheetTemplated?.scheduleType;
    //Excecuted and Non-Admin - Do not

    if (scheduleType && scheduleType.toLowerCase() !== "ondemand" && !isOperator(this.currentRole)) {
      this.updateLogsheetStatus("inProgress");
    } else if (this.logSheet.status !== 'executed' && isOperator(this.currentRole) && scheduleType && scheduleType.toLowerCase() !== "ondemand") {
      this.updateLogsheetStatus("inProgress");
    }


    this.dataPoints = this.logSheet?.logSheetTemplated?.dataPointDetails ?? [];
    this.initialDataPoints = structuredClone(this.dataPoints);

    // Load guards for this template
    const templateId = this.logSheet?.logSheetTemplated?.logSheetTemplateId ?? this.logSheet?.logSheetTemplateId;
    if (templateId) {
      this.guardsLoading = true;
      this.guardService.getGuardsForTemplate(templateId).subscribe({
        next: (guards) => {
          this.guards = guards;
          this.guardsLoaded = true;
          this.guardsLoading = false;
        },
        error: () => {
          this.guards = [];
          this.guardsLoaded = true;
          this.guardsLoading = false;
        }
      });
    } else {
      this.guardsLoaded = true;
    }

    if (templateId) {
      this.notificationConfigService.getByLogSheetTemplateIdBatch(templateId).subscribe({
        next: (configs) => {
          this.perItemConfig = configs.find(c => c.notificationType === 'Per Item Notification') ?? null;
          this.submissionConfig = configs.find(c => c.notificationType === 'Form Submission Notification') ?? null;
        },
        error: () => { }
      });
    }

    // Load all entries from the logsheet
    this.loadAllEntries();

    if ((this.logSheet?.nonAdminEditCount ?? 0) + (this.logSheet?.adminEditCount ?? 0) > 0) {
      this.retrievePreviousVersions();
    }
  }

  ngOnInit(): void {
    if (this.runtimeMode) {
      this.sections = Array.isArray(this.runtimeSections) ? this.runtimeSections : [];
      this.activeVersion = this.runtimeEditable ? 0 : 1;
      this.stampDurations();
      return;
    }

    this.refreshSnapshotTimer = window.setInterval(() => this.persistStandardRefreshSnapshot(), 750);

    const stateLogSheet = history.state.logSheet ?? {};

    // Default to static until we know for sure.
    this.isDynamicForm = false;

    const routeLogSheetId = Number(this.route.snapshot.queryParamMap?.get('logId'));
    const logSheetId = stateLogSheet?.logSheetId || routeLogSheetId;
    if (!logSheetId) {
      this.initFromLogSheet(stateLogSheet);
      return;
    }

    // Always fetch the full logsheet from the API so `formType` is reliable.
    this.logSheetService.retrieveActualLogsheet(String(logSheetId)).subscribe({
      next: (fullLogSheet) => this.initFromLogSheet(fullLogSheet ?? stateLogSheet),
      error: () => this.initFromLogSheet(stateLogSheet),
    });
  }

  loadAllEntries(): void {
    // Check if there are any saved entries in the dataPointValue arrays
    if (this.dataPoints && this.dataPoints.length > 0 && this.dataPoints[0]["dataPointValue"]?.length > 0) {
      // Group dataPointValues by logSheetEntryId to identify separate entries
      const uniqueEntryIds = new Set<number>();
      this.dataPoints.forEach((dp: any) => {
        dp["dataPointValue"].forEach((dpv: any) => {
          if (dpv.logSheetEntryId) {
            uniqueEntryIds.add(dpv.logSheetEntryId);
          }
        });
      });

      const entryIdList = Array.from(uniqueEntryIds).sort((a, b) => a - b);

      // Create sections for each unique entry ID
      this.sections = [];
      for (let entryIdx = 0; entryIdx < entryIdList.length; entryIdx++) {
        const entryId = entryIdList[entryIdx];
        const dataPointValues = [];

        // For each data point, find the value with this entryId
        for (let dp of this.dataPoints) {
          const dpValue = dp["dataPointValue"].find((v: any) => v.logSheetEntryId === entryId);

          dataPointValues.push({
            dataPointId: dp["dataPointId"],
            value: dpValue?.["value"] || "",
            note: dpValue?.["note"] || "",
          });
        }

        this.sections.push({
          id: entryIdx + 1,
          name: `Entry ${entryIdx + 1}`,
          dataPointValues: dataPointValues,
          dataPoints: this.getSectionDataPoints(),
          isSubmitted: this.currentRoute.includes("/forms") ? true : false, // Mark loaded entries as already submitted
          entryId: entryId,
        });
      }

      this.sectionCount = entryIdList.length;
      this.submittedSectionCount = entryIdList.length;
      this.nextEntryId = entryIdList.length > 0 ? Math.max(...entryIdList) + 1 : 1;

      // Set initial values
      let allDataPointValues: any = [];
      for (let section of this.sections) {
        allDataPointValues = [...allDataPointValues, ...section.dataPointValues];
      }
      this.initialDataPointValues = JSON.parse(JSON.stringify(allDataPointValues));
      this.dataPointValues = allDataPointValues;
    } else {
      // No existing entries, create one empty section
      this.initialDataPointValues = this.getDataPointValues(this.dataPoints);
      this.dataPointValues = this.getDataPointValues(this.dataPoints);
      this.initializeSections();
    }

    // Safety check: ensure sections array is never empty
    if (!this.sections || this.sections.length === 0) {
      console.warn('Sections array is empty! Force initializing...');
      this.initialDataPointValues = this.getDataPointValues(this.dataPoints);
      this.dataPointValues = this.getDataPointValues(this.dataPoints);
      this.initializeSections();
    }

    this.applyInitialConditionalVisibility();
    // Populate auto fields up front so they're visible while filling the form.
    // (Submitted-At is re-stamped at submit, so this is just a live preview.)
    this.stampReferenceNumbers();
    this.stampAutoTimestamps();
    if (!this.runtimeMode) this.restoreStandardRefreshSnapshot();
  }

  private standardRefreshStateKey(): string {
    return `log-sheet:${this.logSheet?.logSheetId || 0}`;
  }

  private restoreStandardRefreshSnapshot(): void {
    const logSheetId = Number(this.logSheet?.logSheetId);
    if (!logSheetId) {
      this.refreshRestoreComplete = true;
      return;
    }

    const snapshot = this.refreshState.load<LogSheetRefreshSnapshot>(this.standardRefreshStateKey());
    if (snapshot?.logSheetId === logSheetId && Array.isArray(snapshot.sections) && snapshot.sections.length > 0) {
      this.sections = snapshot.sections.map((section) => ({
        id: section.id,
        name: section.name,
        entryId: section.entryId,
        isSubmitted: !!section.isSubmitted,
        dataPointValues: structuredClone(section.dataPointValues || []),
        // Field definitions always come from the freshly loaded API response. Only the
        // user's values and notes are restored from browser state.
        dataPoints: this.getSectionDataPoints(),
      }));
      this.deletedEntryIds = [...(snapshot.deletedEntryIds || [])];
      this.sectionCount = Number(snapshot.sectionCount) || this.sections.length;
      this.submittedSectionCount = Number(snapshot.submittedSectionCount) || 0;
      this.nextEntryId = Number(snapshot.nextEntryId) || this.sections.length + 1;
      this.hiddenFieldValueCache = structuredClone(snapshot.hiddenFieldValueCache || {});
      this.dataPointValues = this.sections.flatMap((section: any) => section.dataPointValues || []);
      this.applyInitialConditionalVisibility();
    }
    this.refreshRestoreComplete = true;
  }

  private persistStandardRefreshSnapshot(): void {
    if (
      this.runtimeMode ||
      this.refreshPersistenceDisabled ||
      !this.refreshRestoreComplete ||
      !this.logSheet?.logSheetId ||
      !Array.isArray(this.sections) ||
      this.sections.length === 0
    ) return;

    this.refreshState.save<LogSheetRefreshSnapshot>(this.standardRefreshStateKey(), {
      logSheetId: Number(this.logSheet.logSheetId),
      sections: this.sections.map((section) => ({
        id: section.id,
        name: section.name,
        entryId: section.entryId,
        isSubmitted: !!section.isSubmitted,
        dataPointValues: structuredClone(section.dataPointValues || []),
      })),
      deletedEntryIds: [...this.deletedEntryIds],
      sectionCount: this.sectionCount,
      submittedSectionCount: this.submittedSectionCount,
      nextEntryId: this.nextEntryId,
      hiddenFieldValueCache: structuredClone(this.hiddenFieldValueCache),
    });
  }

  private clearStandardRefreshSnapshot(): void {
    this.refreshState.remove(this.standardRefreshStateKey());
    this.refreshPersistenceDisabled = true;
  }

  getDataPointValues(value: any) {
    let data = [];
    for (let e of value) {
      data.push({
        dataPointId: e["dataPointId"],
        value: "",
        note: "",
      });
    }

    return data;
  }

  private getSectionDataPoints(): any[] {
    const templateDataPoints =
      this.initialDataPoints?.length > 0 ? this.initialDataPoints : this.dataPoints;
    return structuredClone(templateDataPoints ?? []);
  }

  private applyInitialConditionalVisibility(): void {
    for (let sectionIdx = 0; sectionIdx < this.sections.length; sectionIdx++) {
      this.applyInitialConditionalVisibilityForSection(sectionIdx);
    }
  }

  private applyInitialConditionalVisibilityForSection(sectionIdx: number): void {
    const section = this.sections[sectionIdx];
    if (!section?.dataPoints?.length) {
      return;
    }

    // Rehydrate visibility from saved values without clearing hidden field data.
    for (const dataPoint of section.dataPoints) {
      const triggerConditions = dataPoint?.triggerConditions;
      if (!triggerConditions) {
        continue;
      }

      const hiddenFieldDataPoint =
        triggerConditions.hiddenFieldDataPoint ?? triggerConditions.hiddenFieldId;
      if (hiddenFieldDataPoint == null) {
        continue;
      }

      const sourceValue = section.dataPointValues?.find(
        (dp: any) => Number(dp.dataPointId) === Number(dataPoint.dataPointId)
      )?.value ?? "";

      const hiddenField = section.dataPoints.find(
        (dp: any) => Number(dp.dataPointId) === Number(hiddenFieldDataPoint)
      );
      if (!hiddenField) {
        continue;
      }

      hiddenField["hiddenByDefault"] = !this.isFieldConditionMet(
        sourceValue,
        dataPoint["dataType"],
        triggerConditions["condition"],
        triggerConditions["value"]
      );
    }
  }

  customComparator(value1: any, value2: any): boolean | undefined {
    if (typeof value1 === "number" || typeof value2 === "number") {
      return value1 == value2; // Loose equality
    }
    return undefined; // Let _.isEqualWith handle strict equality
  }

  isEnabled() {
    if (this.activeVersion != 0) {
      return false;
    }

    // Check if there are any sections and at least one section is filled
    if (!this.sections || this.sections.length === 0) {
      return false;
    }

    // Disable while guard configuration is still loading
    if (this.guardsLoading) {
      return false;
    }

    // Collect all current section data
    let allCurrentDataPointValues: any = [];
    for (let section of this.sections) {
      allCurrentDataPointValues = [...allCurrentDataPointValues, ...section.dataPointValues];
    }

    // Check if there are changes from the initial state
    const hasChanges = !_.isEqualWith(
      this.initialDataPointValues,
      allCurrentDataPointValues,
      this.customComparator
    );

    if (!hasChanges) {
      return false;
    }

    return true;
  }

  areAllDataPointsValid(): boolean {
    for (let i = 0; i < this.dataPointValues?.length; i++) {
      // Skip if there's a note (notes can substitute for values)
      if (this.dataPointValues[i]["note"]?.length > 0) {
        continue;
      }

      const value = this.dataPointValues[i]["value"];
      const rawValue = value == null ? "" : value.toString();
      const dataType = this.dataPoints[i]["dataType"];
      const isRequired = this.isFieldRequired(this.dataPoints[i]);
      const numerator = dataType === "Fraction" ? this.extractNumerator(rawValue) : "";
      const denominator = dataType === "Fraction" ? this.extractDenominator(rawValue) : "";
      const isFieldEmpty = rawValue.trim() === "";
      const isFractionFullyEmpty = dataType === "Fraction" && numerator === "" && denominator === "";
      const isFractionPartiallyFilled =
        dataType === "Fraction" &&
        (numerator === "" || denominator === "") &&
        !isFractionFullyEmpty;

      if (!isRequired && (isFieldEmpty || isFractionFullyEmpty)) {
        continue;
      }

      // Check if value is empty for required fields
      if (isFieldEmpty || isFractionPartiallyFilled) {
        return false;
      }

      // Check Fraction type has both numerator and denominator
      if (dataType === "Fraction") {
        if (
          parseInt(denominator) === 0
        ) {
          return false;
        }
      }

      // Check Text (Short) type has minimum length
      if (dataType === "Text (Short)" && rawValue.length < 2) {
        return false;
      }

      // Check Text (Long) type has minimum length
      if (dataType === "Text (Long)" && rawValue.length < 2) {
        return false;
      }

      // Check Number type is within range
      if (dataType === "Number") {
        const numValue = parseFloat(rawValue);
        if (
          numValue < this.dataPoints[i]["dataPointMin"] ||
          numValue > this.dataPoints[i]["dataPointMax"]
        ) {
          return false;
        }
      }
    }

    return true;
  }

  @HostListener("window:beforeunload", ["$event"])
  unloadHandler(event: Event) {
    // Workflow runtime snapshots are owned by the parent component. Avoid sending a
    // status beacon with an undefined log-sheet id from its shared field renderer.
    if (this.runtimeMode) return;
    this.persistStandardRefreshSnapshot();
    if (this.currentRoute.includes('/forms')) {
      if (this.hasSubmitted) return;
      //Only revert the log sheet status if admin is editing it
      this.updateLogsheetStatus(this.logSheet.status, "0");
    }
    const status = { status: this.logSheet.status, currentUser: "0" };
    const isSuccess = navigator.sendBeacon(
      this.baseUrl + "/logsheet/logs/" + this.logSheet.logSheetId,
      JSON.stringify(status)
    );

    event.returnValue = isSuccess;
  }

  extractNumerator(value: string) {
    if (value == null) {
      return "";
    }
    value = value.toString();
    if (value.length === 0) {
      return "";
    }
    return value.split("/")[0];
  }

  extractDenominator(value: string) {
    if (value == null) {
      return "";
    }
    value = value.toString();
    if (value.length === 0) {
      return "";
    }
    return value.split("/")[1];
  }

  isCompleteFraction(fraction: string) {
    return fraction.split("/").length == 2 ? true : false
  }

  onNumeratorChange(e: Event, idx: number, sectionIdx?: number, valuationOptions?: any) {
    const targetSection = sectionIdx !== undefined ? this.sections[sectionIdx] : this;
    const dataPointValues = sectionIdx !== undefined ? targetSection.dataPointValues : this.dataPointValues;

    if (dataPointValues[idx]?.value?.length === 0) {
      dataPointValues[idx].value = `${(e?.target as HTMLInputElement).value
        }/`;
      if (sectionIdx !== undefined) {
        this.onInputChange(sectionIdx);
      }
      return;
    }
    const denominator = this.extractDenominator(
      dataPointValues[idx]?.value
    );
    dataPointValues[idx].value = `${(e.target as HTMLInputElement).value
      }/${denominator}`;
    if (sectionIdx !== undefined) {
      this.onInputChange(sectionIdx);

      if (valuationOptions && this.isCompleteFraction(dataPointValues[idx].value)) {
        this.evaluateFieldCondition(
          dataPointValues[idx].value,
          valuationOptions?.[0],
          valuationOptions?.[1],
          valuationOptions?.[2],
          valuationOptions?.[3],
          valuationOptions?.[4]
        )
      }
    }
  }

  onDenominatorChange(e: any, idx: number, sectionIdx?: number, valuationOptions?: any) {
    const targetSection = sectionIdx !== undefined ? this.sections[sectionIdx] : this;
    const dataPointValues = sectionIdx !== undefined ? targetSection.dataPointValues : this.dataPointValues;

    if (dataPointValues[idx]?.value?.length === 0) {
      dataPointValues[idx].value = `/${e.target.value}`;
      if (sectionIdx !== undefined) {
        this.onInputChange(sectionIdx);
      }
      return;
    }
    const numerator = this.extractNumerator(dataPointValues[idx]?.value);
    dataPointValues[idx].value = `${numerator}/${e.target.value}`;
    if (sectionIdx !== undefined) {
      this.onInputChange(sectionIdx);

      if (valuationOptions && this.isCompleteFraction(dataPointValues[idx].value)) {
        this.evaluateFieldCondition(
          dataPointValues[idx].value,
          valuationOptions?.[0],
          valuationOptions?.[1],
          valuationOptions?.[2],
          valuationOptions?.[3],
          valuationOptions?.[4]
        )
      }
    }
  }

  updateLogsheetStatus(status: string, currentUser?: string) {
    this.logSheetService
      .updateLogSheetStatus(this.logSheet.logSheetId, {
        status: status,
        currentUser,
      })
      .subscribe({
        next: (res) => { },
        error: (err) => {
          this.errorService.handleError(
            err,
            "Could not update logsheet status"
          );
        },
      });
  }

  ngOnDestroy(): void {
    this.persistStandardRefreshSnapshot();
    if (this.refreshSnapshotTimer !== undefined) window.clearInterval(this.refreshSnapshotTimer);
    // reverts logsheet status to original state
    if (this.currentRoute.includes('/forms') && !this.hasSubmitted) {
      //Only revert the status when admin is editing it
      this.updateLogsheetStatus(this.logSheet.status, "0");
    }
  }

  dateTimeFormatter(date: string, idx: number, sectionIdx?: number, valuationOptions?: any): void {
    const formattedDate = this.datePipe.transform(date, "MMMM d, y, h:mm a");
    if (sectionIdx !== undefined) {
      this.sections[sectionIdx].dataPointValues[idx]["value"] = formattedDate;
      this.onInputChange(sectionIdx);

      if (valuationOptions) {
        this.evaluateFieldCondition(
          this.sections[sectionIdx].dataPointValues[idx]["value"],
          valuationOptions?.[0],
          valuationOptions?.[1],
          valuationOptions?.[2],
          valuationOptions?.[3],
          valuationOptions?.[4]
        )
      }
    } else {
      this.dataPointValues[idx]["value"] = formattedDate;
    }
  }

  retrievePreviousVersions(): void {
    this.logSheetFormService
      .retrieveLogSheetVersions(this.logSheet["logSheetId"])
      .subscribe({
        next: (response) => {
          this.versions = response[0]["logSheetTemplated"]["dataPointDetails"];
        },
        error: (err) => {
          this.errorMessage = this.errorService.handleRetrievalErrors(err);
        },
      });
  }

  setActiveVersion(value: number): void {
    this.activeVersion = value;
  }
  toggleVersions(): void {
    this.isVersionMenuVisible = !this.isVersionMenuVisible;
  }

  toggleNoteMenu(idx: number, sectionIdx?: number): void {
    this.selectedDataPointIdx = idx;
    this.selectedSectionIdx = sectionIdx !== undefined ? sectionIdx : 0;
    this.isNoteMenuVisible = !this.isNoteMenuVisible;
  }

  toggleNoteModal(): void {
    this.isNoteMenuVisible = false;
    this.showNoteModal = !this.showNoteModal;
    if (this.showNoteModal) {
      const dataPointValues = this.sections[this.selectedSectionIdx]?.dataPointValues || this.dataPointValues;
      this.note = dataPointValues[this.selectedDataPointIdx]["note"] ?? "";
    } else {
      this.note = "";
    }
  }

  showDeactivateModal(): void {
    this.isNoteMenuVisible = false;
    const dataPoints = this.sections[this.selectedSectionIdx]?.dataPoints || this.dataPoints;
    this.deactivateText.message1 =
      "You are about to delete " +
      dataPoints[this.selectedDataPointIdx]["dataPointName"] +
      " Note. This change is irreversible. Do you wish to proceed? ";
    this.showDeactivateMenu = true;
  }

  isNoteSubmitBtnEnabled(): boolean {
    const dataPointValues = this.sections[this.selectedSectionIdx]?.dataPointValues || this.dataPointValues;
    if (
      this.note.trim().length < 2 ||
      (this.isEditing(this.selectedSectionIdx) &&
        this.note == dataPointValues[this.selectedDataPointIdx]["note"])
    ) {
      return false;
    }
    return true;
  }

  isEditing(sectionIdx?: number): boolean {
    const dataPointValues = sectionIdx !== undefined && this.sections[sectionIdx]
      ? this.sections[sectionIdx].dataPointValues
      : this.dataPointValues;

    if (
      dataPointValues[this.selectedDataPointIdx]["note"] == "" ||
      dataPointValues[this.selectedDataPointIdx]["note"] == null
    ) {
      return false;
    }
    return true;
  }

  invalidFields: any = {};

  isEmpty(obj: any) {
    return Object.entries(obj).length === 0;
  }

  hasTrueForDPId(dpId: string): boolean {
    if (this.singleSelect.hasOwnProperty(dpId)) {
      const dpIdValues = Object.values(this.singleSelect[dpId]);
      return dpIdValues.includes(true);
    }
    return false;
  }

  private resolveBooleanFlag(value: any, fallback: boolean): boolean {
    if (value === true || value === "true" || value === 1 || value === "1") {
      return true;
    }
    if (value === false || value === "false" || value === 0 || value === "0") {
      return false;
    }
    return fallback;
  }

  isFieldRequired(dataPoint: any): boolean {
    const isHidden = this.resolveBooleanFlag(dataPoint?.["hiddenByDefault"], false);
    if (isHidden) {
      return false;
    }

    const mandatoryFlag =
      dataPoint?.["mandatoryWhenVisible"] ??
      dataPoint?.["requiredWhenVisible"];

    const conditionRequiredFlag =
      dataPoint?.fieldCondition?.requiredWhenVisible ??
      dataPoint?.triggerConditions?.requiredWhenVisible;

    const isMandatory = this.resolveBooleanFlag(mandatoryFlag, true);
    const isRequiredWhenVisible = this.resolveBooleanFlag(conditionRequiredFlag, false);

    return isMandatory || isRequiredWhenVisible;
  }

  private getFieldValidationError(section: any, sectionIdx: number, fieldIdx: number): string | null {
    const fieldValue = section?.dataPointValues?.[fieldIdx];
    const dataPoint = section?.dataPoints?.[fieldIdx];
    if (!fieldValue || !dataPoint) {
      return null;
    }

    // Auto fields are system-generated/computed and never user-fillable, so an
    // empty one (e.g. a Duration whose referenced date field was deactivated)
    // must never block submission.
    if (['Duration', 'Timestamp (Auto)', 'Reference Number'].includes(dataPoint['dataType'])) {
      return null;
    }

    if ((fieldValue["note"] ?? "").length > 0) {
      return null;
    }

    const value = fieldValue["value"];
    const rawValue = value == null ? "" : value.toString();
    const trimmedValue = rawValue.trim();
    const dataType = dataPoint["dataType"];
    const isRequired = this.isFieldRequired(dataPoint);

    const numerator = dataType === "Fraction" ? this.extractNumerator(rawValue) : "";
    const denominator = dataType === "Fraction" ? this.extractDenominator(rawValue) : "";
    const isFieldEmpty = trimmedValue === "";
    const isFractionFullyEmpty = dataType === "Fraction" && numerator === "" && denominator === "";
    const isFractionPartiallyFilled =
      dataType === "Fraction" &&
      (numerator === "" || denominator === "") &&
      !isFractionFullyEmpty;

    if (!isRequired && (isFieldEmpty || isFractionFullyEmpty)) {
      return null;
    }

    if (isFieldEmpty || isFractionPartiallyFilled) {
      return this.errorMsgEmpty;
    }

    if ((dataType === "Text (Short)" || dataType === "Text (Long)") && rawValue.length < 2) {
      return this.errorMsgMinValue;
    }

    if (dataType === "Number") {
      const parsedNumber = parseFloat(rawValue);
      if (Number.isNaN(parsedNumber)) {
        return this.errorMsgNAN;
      }
      if (
        parsedNumber < dataPoint["dataPointMin"] ||
        parsedNumber > dataPoint["dataPointMax"]
      ) {
        return this.errorMsgRange;
      }
    }

    if (dataType === "Fraction" && parseInt(denominator, 10) === 0) {
      return this.errorMsgDenominator;
    }

    if (dataType === "Date") {
      this.validateManualDateInput(fieldIdx, sectionIdx);
      if (fieldValue["dateFormatError"]) {
        return this.errorMsgInvalidDate;
      }
    }

    return null;
  }

  validateFields(): void {
    let invalidFields: Record<string, string> = {};

    // Validate all sections
    for (let sectionIdx = 0; sectionIdx < this.sections.length; sectionIdx++) {
      const section = this.sections[sectionIdx];
      const sectionDataPointValues = section.dataPointValues;

      for (let i = 0; i < sectionDataPointValues?.length; i++) {
        const fieldKey = `${sectionIdx}_${i}`;
        const error = this.getFieldValidationError(section, sectionIdx, i);
        if (error) {
          invalidFields = { ...invalidFields, [fieldKey]: error };
        }
      }
    }

    this.invalidFields = invalidFields;
  }

  updateNote(): void {
    let isEditingNote = this.isEditing(this.selectedSectionIdx);
    const dataPointValues = this.sections[this.selectedSectionIdx]?.dataPointValues || this.dataPointValues;
    dataPointValues[this.selectedDataPointIdx]["note"] = this.note.trim();
    if (this.selectedSectionIdx !== undefined) {
      this.onInputChange(this.selectedSectionIdx);
    }
    this.toggleNoteModal();
    this.toastService.showToast = true;
    this.toastService.toastText = isEditingNote ? "Note Edited!" : "Note Added!";
    setTimeout(() => {
      this.toastService.showToast = false;
    }, 5000);
  }

  deleteNote(): void {
    const dataPointValues = this.sections[this.selectedSectionIdx]?.dataPointValues || this.dataPointValues;
    dataPointValues[this.selectedDataPointIdx]["note"] = "";
    if (this.selectedSectionIdx !== undefined) {
      this.onInputChange(this.selectedSectionIdx);
    }

    this.note = "";
    this.showDeactivateMenu = false;
    this.toastService.showToast = true;
    this.toastService.toastText = "Note Deleted!";
    setTimeout(() => {
      this.toastService.showToast = false;
    }, 5000);
  }

  setCurrentTimestamp(idx: number, sectionIdx?: number, valuationOptions?: any): void {
    let date = new Date().toISOString();
    const formattedDate = this.datePipe.transform(date, "MMMM d, y h:mm a");
    if (sectionIdx !== undefined) {
      this.sections[sectionIdx].dataPointValues[idx]["value"] = formattedDate;
      if (valuationOptions) {
        this.evaluateFieldCondition(
          this.sections[sectionIdx].dataPointValues[idx]["value"],
          valuationOptions?.[0],
          valuationOptions?.[1],
          valuationOptions?.[2],
          valuationOptions?.[3],
          valuationOptions?.[4]
        )
      }
      this.onInputChange(sectionIdx);
    } else {
      this.dataPointValues[idx]["value"] = formattedDate;
    }
  }

  dateFormatter(date: string, idx: number, sectionIdx?: number): void {
    // Validate date before formatting
    if (!date || !this.isValidDate(date)) {
      return;
    }
    const formattedDate = this.datePipe.transform(date, "dd-MMM-yyyy");
    if (sectionIdx !== undefined) {
      this.sections[sectionIdx].dataPointValues[idx]["value"] = formattedDate;
      this.onInputChange(sectionIdx);
    } else {
      this.dataPointValues[idx]["value"] = formattedDate;
    }
  }

  // Validates if a date string is valid
  isValidDate(dateString: string): boolean {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  }

  setCurrentDate(idx: number, sectionIdx?: number, valuationOptions?: any): void {
    let date = new Date().toISOString();
    const formattedDate = this.datePipe.transform(date, "dd-MMM-yyyy");
    if (sectionIdx !== undefined) {
      this.sections[sectionIdx].dataPointValues[idx]["value"] = formattedDate;
      this.sections[sectionIdx].dataPointValues[idx]["dateFormatError"] = false;

      if (valuationOptions) {
        this.evaluateFieldCondition(
          this.sections[sectionIdx].dataPointValues[idx]["value"],
          valuationOptions?.[0],
          valuationOptions?.[1],
          valuationOptions?.[2],
          valuationOptions?.[3],
          valuationOptions?.[4]
        )
      }
      this.onInputChange(sectionIdx);
    } else {
      this.dataPointValues[idx]["value"] = formattedDate;
      this.dataPointValues[idx]["dateFormatError"] = false;
    }
  }

  // Validate manual date input in dd-MMM-yyyy format
  validateManualDateInput(idx: number, sectionIdx?: number, valuationOptions?: any): void {
    const dataPointValues = sectionIdx !== undefined
      ? this.sections[sectionIdx].dataPointValues
      : this.dataPointValues;

    const value = dataPointValues[idx]["value"];

    // Allow empty values
    if (!value || value.trim() === "") {
      dataPointValues[idx]["dateFormatError"] = false;
      return;
    }

    // Check if the format matches dd-MMM-yyyy (e.g., 29-Jan-2026)
    const datePattern = /^(\d{1,2})-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-(\d{4})$/i;
    const match = value.match(datePattern);

    if (!match) {
      dataPointValues[idx]["dateFormatError"] = true;
      return;
    }

    // Validate the date is actually valid
    const day = parseInt(match[1], 10);
    const monthStr = match[2];
    const year = parseInt(match[3], 10);

    // Validate year is in reasonable range (1900-2100)
    if (year < 1900 || year > 2100) {
      dataPointValues[idx]["dateFormatError"] = true;
      return;
    }

    const months: { [key: string]: number } = {
      'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
      'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
    };

    const month = months[monthStr.toLowerCase()];
    const testDate = new Date(year, month, day);

    // Check if the date is valid (handles invalid days like Feb 30)
    if (testDate.getFullYear() !== year ||
      testDate.getMonth() !== month ||
      testDate.getDate() !== day) {
      dataPointValues[idx]["dateFormatError"] = true;
      return;
    }

    // Format is valid - normalize the format (capitalize month abbreviation)
    const formattedDate = this.datePipe.transform(testDate, "dd-MMM-yyyy");
    dataPointValues[idx]["value"] = formattedDate;
    dataPointValues[idx]["dateFormatError"] = false;

    if (valuationOptions) {
      this.evaluateFieldCondition(
        formattedDate,
        valuationOptions?.[0],
        valuationOptions?.[1],
        valuationOptions?.[2],
        valuationOptions?.[3],
        valuationOptions?.[4]
      )
    }

    if (sectionIdx !== undefined) {
      this.onInputChange(sectionIdx);
    }
  }

  // Handle date picker selection
  onDatePickerChange(event: any, idx: number, sectionIdx?: number, valuationOptions?: any): void {
    if (!event || !event.value) return;

    const formattedDate = this.datePipe.transform(event.value, "dd-MMM-yyyy");
    if (sectionIdx !== undefined) {
      this.sections[sectionIdx].dataPointValues[idx]["value"] = formattedDate;
      this.sections[sectionIdx].dataPointValues[idx]["dateFormatError"] = false;
      this.onInputChange(sectionIdx);

      if (valuationOptions) {
        this.evaluateFieldCondition(
          this.sections[sectionIdx].dataPointValues[idx]["value"],
          valuationOptions?.[0],
          valuationOptions?.[1],
          valuationOptions?.[2],
          valuationOptions?.[3],
          valuationOptions?.[4]
        )
      }
    } else {
      this.dataPointValues[idx]["value"] = formattedDate;
      this.dataPointValues[idx]["dateFormatError"] = false;
    }
  }

  captureGPS(idx: number, sectionIdx?: number, valuationOptions?: any): void {
    if (!navigator.geolocation) {
      this.toastService.showToast = true;
      this.toastService.toastText = "Geolocation is not supported by this browser.";
      setTimeout(() => { this.toastService.showToast = false; }, 5000);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const now = new Date();
        const formattedDate = this.datePipe.transform(now, "dd-MMM-yyyy hh:mm a") || "";
        const lat = position.coords.latitude.toFixed(4);
        const lng = position.coords.longitude.toFixed(4);
        const alt = position.coords.altitude != null
          ? position.coords.altitude.toFixed(2) + " m"
          : "N/A";

        let locationName = "Unknown";
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
            { headers: { "User-Agent": "SmartForms/2.0" } }
          );
          const data = await response.json();
          const addr = data.address || {};
          const locationParts = [
            addr.road,
            addr.suburb || addr.neighbourhood || addr.quarter || addr.village || addr.town || addr.city,
            addr.country,
          ].filter(Boolean);
          locationName = locationParts.join(", ") || "Unknown";
        } catch {
          // fall back silently
        }

        const gpsValue = `${formattedDate} | Lat: ${lat} | Lng: ${lng} | Alt: ${alt} | Location: ${locationName}`;

        if (sectionIdx !== undefined) {
          this.sections[sectionIdx].dataPointValues[idx]["value"] = gpsValue;
          this.onInputChange(sectionIdx);

          if (valuationOptions) {
            this.evaluateFieldCondition(
              this.sections[sectionIdx].dataPointValues[idx]["value"],
              valuationOptions?.[0],
              valuationOptions?.[1],
              valuationOptions?.[2],
              valuationOptions?.[3],
              valuationOptions?.[4]
            )
          }
        } else {
          this.dataPointValues[idx]["value"] = gpsValue;
        }
      },
      (error) => {
        let errorMsg = "Unable to retrieve location.";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = "Location permission denied. Please allow location access in your browser.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = "Location information is unavailable.";
            break;
          case error.TIMEOUT:
            errorMsg = "Location request timed out.";
            break;
        }
        this.toastService.showToast = true;
        this.toastService.toastText = errorMsg;
        setTimeout(() => { this.toastService.showToast = false; }, 5000);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  getGpsField(value: string, field: string): string {
    if (!value) return "";
    const parts = value.split(" | ");
    switch (field) {
      case "time":
        return parts[0] || "";
      case "lat":
        return parts[1]?.replace("Lat: ", "") || "";
      case "lng":
        return parts[2]?.replace("Lng: ", "") || "";
      case "alt":
        return parts[3]?.replace("Alt: ", "") || "";
      case "location":
        return parts[4]?.replace("Location: ", "") || "";
      default:
        return "";
    }
  }

  printPage() {
    window.print();
  }

  initializeSections(): void {
    // Initialize with one empty section (only called when no existing data)
    this.sections = [
      {
        id: 1,
        name: "Entry 1",
        dataPointValues: JSON.parse(JSON.stringify(this.dataPointValues)),
        dataPoints: this.getSectionDataPoints(),
        isSubmitted: false,
        entryId: 1,
      },
    ];
    this.sectionCount = 1;
    this.submittedSectionCount = 0;
    this.nextEntryId = 2;
  }

  addSection(): void {
    if (!this.isDynamicForm || this.isAddingSection) {
      return;
    }
    this.isAddingSection = true;

    if (this.perItemConfig && this.sections.length > 0) {
      const completedSection = this.sections[this.sections.length - 1];
      this.notificationSendService.sendInProgress(
        this.logSheet['logSheetId'], this.sections.length, completedSection, this.dataPoints
      );
    }

    // Always allow adding a new section after submission
    this.sectionCount++;
    const newSection = {
      id: this.sectionCount,
      name: `Entry ${this.sectionCount}`,
      dataPointValues: this.getDataPointValues(this.dataPoints),
      dataPoints: this.getSectionDataPoints(),
      isSubmitted: false,
      entryId: this.nextEntryId,
    };
    // Add new section at the end of the array
    this.sections.push(newSection);
    this.applyInitialConditionalVisibilityForSection(this.sections.length - 1);
    this.nextEntryId++;

    // Scroll to the new section after a short delay to allow rendering
    setTimeout(() => {
      this.scrollToSection(this.sectionCount);
      this.isAddingSection = false;
    }, 100);
  }

  scrollToSection(sectionId: number): void {
    const sectionIndex = this.sections.findIndex((s: any) => s.id === sectionId);
    if (sectionIndex !== -1) {
      const sectionElements = document.querySelectorAll('.form-section');
      if (sectionElements[sectionIndex]) {
        sectionElements[sectionIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  removeSection(sectionId: number): void {
    if (!this.isDynamicForm) {
      return;
    }
    const section = this.sections.find((s: any) => s.id === sectionId);
    const isEmpty = section?.dataPointValues?.every(
      (dp: any) => !dp.value || dp.value.toString().trim() === ''
    );
    if (isEmpty) {
      this.pendingDeleteSectionId = sectionId;
      this.confirmDeleteSection();
      return;
    }
    this.pendingDeleteSectionId = sectionId;
    this.showDeleteFormEntryMenu = true;
  }

  confirmDeleteSection(): void {
    const sectionId = this.pendingDeleteSectionId;
    if (sectionId === null) return;

    const sectionToRemove = this.sections.find((s: any) => s.id === sectionId);
    if (this.sections.length > 1 && sectionToRemove) {
      if (sectionToRemove.entryId && typeof sectionToRemove.entryId === 'number') {
        this.deletedEntryIds.push(sectionToRemove.entryId);
      }
      this.sections = this.sections.filter((section: any) => section.id !== sectionId);
      this.sections.forEach((section: any, index: number) => {
        section.name = `Entry ${index + 1}`;
      });
      this.sectionCount = this.sections.length;
    }

    this.showDeleteFormEntryMenu = false;
    this.pendingDeleteSectionId = null;

    this.toastService.showToast = true;
    this.toastService.toastText = 'Form Entry deleted successfully';
    setTimeout(() => {
      this.toastService.showToast = false;
    }, 5000);
  }

  isSectionValid(section: any): boolean {
    const sectionIdx = this.sections.findIndex((s: any) => s.id === section?.id);
    const resolvedSectionIdx = sectionIdx >= 0 ? sectionIdx : 0;
    for (let i = 0; i < section.dataPointValues?.length; i++) {
      if (this.getFieldValidationError(section, resolvedSectionIdx, i)) {
        return false;
      }
    }
    return true;
  }

  hasIncompleteUnsubmittedSections(): boolean {
    // Check if there are any unsubmitted sections that are not valid
    return this.sections.some((section: any) =>
      !section.isSubmitted && !this.isSectionValid(section)
    );
  }

  getDataPointIndexForSection(sectionIdx: number, dpIdx: number): number {
    // Helper method to calculate the global data point index
    return sectionIdx * this.dataPoints.length + dpIdx;
  }

  private getAllDataPointValues(): { dataPointId: number; value: string }[] {
    const result: { dataPointId: number; value: string }[] = [];
    for (const section of this.sections) {
      for (const dpv of section.dataPointValues) {
        result.push({ dataPointId: dpv.dataPointId, value: dpv.value ?? '' });
      }
    }
    return result;
  }

  private runGuardPipeline(queue: SubmissionGuard[], onAllCleared: () => void): void {
    if (queue.length === 0) {
      onAllCleared();
      return;
    }
    this.guardQueue = queue;
    const [current, ...rest] = queue;
    const allValues = this.getAllDataPointValues();
    this.activeGuard = current;
    this.activeGuardMessage = this.guardService.interpolateMessage(current.warningMessage, current, allValues);
    this.pendingSubmitCallback = () => {
      if (current.guardType === 'Acknowledged') {
        const templateId = this.logSheet?.logSheetTemplated?.logSheetTemplateId ?? this.logSheet?.logSheetTemplateId;
        this.guardService.saveAcknowledgment(this.logSheet['logSheetId'], templateId, current.id).subscribe();
      }
      this.activeGuard = null;
      setTimeout(() => this.runGuardPipeline(rest, onAllCleared));
    };
  }

  onGuardProceed(): void {
    if (this.pendingSubmitCallback) {
      this.pendingSubmitCallback();
      this.pendingSubmitCallback = null;
    }
  }

  onGuardGoBack(): void {
    this.activeGuard = null;
    this.pendingSubmitCallback = null;
    this.guardQueue = [];
    this.isSubmitting = false;
  }

  // Auto-stamp "Submitted At" timestamp fields with the current time on save.
  private stampAutoTimestamps(): void {
    const now = new Date().toISOString();
    for (const section of this.sections) {
      if (section.isSubmitted) continue;
      (section.dataPoints || []).forEach((dp: any, i: number) => {
        if (dp['dataType'] === 'Timestamp (Auto)' && section.dataPointValues[i]) {
          section.dataPointValues[i]['value'] = now;
        }
      });
    }
  }

  // Generate a unique reference for empty "Reference Number" fields on save.
  // Config is stored in listOptions as "prefix,padding".
  private stampReferenceNumbers(): void {
    for (const section of this.sections) {
      if (section.isSubmitted) continue;
      (section.dataPoints || []).forEach((dp: any, i: number) => {
        const cell = section.dataPointValues[i];
        if (dp['dataType'] === 'Reference Number' && cell && !cell['value']) {
          const cfg = (dp['listOptions'] || 'REF-,4').split(',');
          const prefix = cfg[0] ?? 'REF-';
          const padding = Math.max(1, parseInt(cfg[1], 10) || 4);
          const num = String(Date.now() % Math.pow(10, padding)).padStart(padding, '0');
          cell['value'] = `${prefix}${num}`;
        }
      });
    }
  }

  // Ensure Duration cells have an initialized "start|end" value so the start/end
  // inputs and the computed-duration display render immediately (notably in runtime
  // mode). Never overwrites an existing value.
  private stampDurations(): void {
    for (const section of this.sections || []) {
      (section.dataPoints || []).forEach((dp: any, i: number) => {
        if (dp['dataType'] !== 'Duration') return;
        const cell = section.dataPointValues?.[i];
        if (!cell) return;
        if (this.isReferenceDuration(dp)) {
          // Reference-based: (re)derive from the two referenced date fields so the computed
          // value rides along in the payload. The lookup only sees siblings within this same
          // section/stage — a cross-stage reference (e.g. Start Date on Stage 1, Duration on
          // Stage 2) can't resolve here, so keep whatever value is already stamped (seeded
          // cross-stage by the workflow runtime form) instead of clobbering it with blank.
          cell['value'] = this.computeReferenceDuration(section, dp) || cell['value'] || '';
        } else if (cell['value'] === undefined || cell['value'] === null) {
          // Self-contained: ensure an initialized "start|end" value so its inputs render.
          cell['value'] = '';
        }
      });
    }
  }

  saveLogSheet(isSaving: boolean): void {
    this.isSaving = isSaving;
    if (this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;
    this.stampAutoTimestamps();
    this.stampReferenceNumbers();
    this.validateFields();
    // Collect only unsaved section data with their entry IDs
    let unsavedSections = this.sections.filter((section: any) => !section.isSubmitted);

    if (Object.keys(this.invalidFields).length == 0 && (unsavedSections.length > 0 || this.deletedEntryIds.length > 0)) {

      if (!isSaving && this.guards.length > 0) {
        const triggeredMap = new Map<number, SubmissionGuard>();
        for (const section of unsavedSections) {
          const sectionValues: { dataPointId: number; value: string }[] =
            section.dataPointValues.map((dpv: any) => ({
              dataPointId: dpv.dataPointId,
              value: dpv.value ?? '',
            }));
          const sectionTriggered = this.guardService.getTriggeredGuards(this.guards, sectionValues);
          for (const g of sectionTriggered) {
            if (!triggeredMap.has(g.id)) {
              triggeredMap.set(g.id, g);
            }
          }
        }
        const triggered = Array.from(triggeredMap.values()).sort(
          (a, b) => a.priority - b.priority
        );
        if (triggered.length > 0) {
          this.runGuardPipeline(triggered, () => this.performSubmit(unsavedSections, isSaving));
          return;
        }
      }
      this.performSubmit(unsavedSections, isSaving);
    } else {
      this.isSubmitting = false;
    }
  }

  private performSubmit(unsavedSections: any[], isSaving: boolean): void {
    // First, delete any removed entries

    const deleteRequests = this.deletedEntryIds.map((entryId) => {

      return this.logSheetFormService
        .deleteEntry(this.logSheet["logSheetId"], entryId)
        .toPromise()
        .then((response) => {
          return response;
        })
        .catch((error) => {
          console.error(`Failed to delete entry ${entryId}:`, error);
          return null;
        });
    });

    // Wait for deletions to complete, then save
    Promise.all(deleteRequests).then(() => {
      this.logSheetFormService
        .saveMultipleEntries(this.logSheet["logSheetId"], unsavedSections, isSaving)
        .toPromise()
        .then((response) => {
          this.clearStandardRefreshSnapshot();
          this.deletedEntryIds = [];

          if (!isSaving) {
            for (let section of unsavedSections) {
              section.isSubmitted = true;
              this.submittedSectionCount++;
            }
          }

          this.nextEntryId = this.submittedSectionCount + 1;

          let allDataPointValues: any = [];
          for (let section of this.sections) {
            allDataPointValues = [...allDataPointValues, ...section.dataPointValues];
          }
          this.initialDataPointValues = JSON.parse(JSON.stringify(allDataPointValues));
          this.dataPointValues = allDataPointValues;

          this.invalidFields = {};

          if (!isSaving) {
            const lastSection = unsavedSections[unsavedSections.length - 1];
            if (lastSection) {
              if (this.submissionConfig) {
                this.notificationSendService.sendSubmission(
                  this.logSheet['logSheetId'], this.sections.length, lastSection, this.dataPoints
                );
              }
              if (this.perItemConfig) {
                this.notificationSendService.sendInProgress(
                  this.logSheet['logSheetId'], this.sections.length, lastSection, this.dataPoints
                );
              }
            }
          }

          if (this.currentRoute.includes('/forms')) {
            this.hasSubmitted = true;
            this.updateLogsheetStatus("executed");
          }

          this.toastService.showToast = true;
          this.toastService.toastText = "Form has been" + (this.isSaving ? " Saved" : " Submitted") + " Successfully";

          setTimeout(() => {
            this.toastService.showToast = false;
            this.isSubmitting = false;
            this.location.back();
          }, 1500);
        })
        .catch((error) => {
          this.isSubmitting = false;
          this.errorService.handleError(error, "Error submitting logsheet changes");
        });
    });
  }
}
